/**
 * Селект — выбор одного значения из короткого списка (до ~15 позиций).
 * Для длинных списков и поиска существует `Combobox`.
 */

import * as React from "react";
import * as RadixSelect from "@radix-ui/react-select";
import { Check, ChevronDown, ChevronUp } from "lucide-react";

import { cn } from "@/shared/lib/cn";

/**
 * Подмена пустого значения.
 *
 * Radix не принимает пустую строку как значение пункта: для него пустое
 * значение означает «ничего не выбрано». А «без набора», «без проекта»,
 * «не указано» — обычные пункты списка, которые человек должен видеть
 * и нажимать. Поэтому пустая строка подменяется меткой, которую нельзя
 * получить снаружи, и возвращается обратно при выборе.
 */
const EMPTY = "__pt_empty__";

/** Значение пункта для Radix: пустое подменяется меткой. */
export function toItemValue(value: string): string {
  return value === "" ? EMPTY : value;
}

/** Обратно наружу: метка снова становится пустым значением. */
export function fromItemValue(value: string): string {
  return value === EMPTY ? "" : value;
}

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps {
  options: readonly SelectOption[];
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  label?: string;
  /** Имя элемента, когда подпись сверху не помещается: полосы фильтров, тулбары. */
  ariaLabel?: string;
  disabled?: boolean;
  invalid?: boolean;
  className?: string;
  id?: string;
}

export function Select({
  options,
  value,
  defaultValue,
  onValueChange,
  placeholder = "Выберите значение",
  label,
  ariaLabel,
  disabled,
  invalid,
  className,
  id,
}: SelectProps): React.ReactElement {
  const generatedId = React.useId();
  const fieldId = id ?? generatedId;
  const chosen = value === "" && !options.some((item) => item.value === "") ? undefined : value;

  return (
    <div className={cn("flex flex-col", className)}>
      {label && (
        <label
          htmlFor={fieldId}
          className="mb-[var(--pt-space-4)] text-[var(--pt-fs-xs)] leading-[var(--pt-lh-xs)] font-medium text-[var(--pt-text-secondary)]"
        >
          {label}
        </label>
      )}
      <RadixSelect.Root
        // Пустая строка значит «не выбрано», пока среди пунктов нет такого
        // значения. Без этой оговорки Radix считал её выбранным пунктом, не
        // находил его и рисовал пустой прямоугольник вместо подсказки: рядом
        // с полем даты стоял безымянный список, о котором нельзя догадаться
        value={chosen === undefined ? undefined : toItemValue(chosen)}
        defaultValue={defaultValue === undefined ? undefined : toItemValue(defaultValue)}
        onValueChange={(next) => onValueChange?.(fromItemValue(next))}
        disabled={disabled}
      >
        <RadixSelect.Trigger
          id={fieldId}
          aria-label={ariaLabel}
          aria-invalid={invalid || undefined}
          className={cn(
            "inline-flex h-[var(--pt-control-h-md)] w-full items-center justify-between gap-[var(--pt-space-6)]",
            "rounded-[var(--pt-radius-3)] border border-[var(--pt-border-control)]",
            "bg-[var(--pt-bg-surface)] px-[var(--pt-pad-x)] text-[var(--pt-font-ui)]",
            "text-[var(--pt-text-primary)] transition-colors duration-[var(--pt-dur-micro)]",
            "hover:border-[var(--pt-border-strong)] focus:outline-none focus:shadow-[var(--pt-ring)]",
            "disabled:cursor-not-allowed disabled:bg-[var(--pt-bg-sunken)] disabled:text-[var(--pt-text-disabled)]",
            "data-[placeholder]:text-[var(--pt-text-tertiary)]",
            invalid && "border-[var(--pt-danger-border)]",
          )}
        >
          {/* Длинное значение обрезается многоточием, а не переносится:
              иначе «Константин Владелец» разваливает строку таблицы */}
          <span className="min-w-0 flex-1 truncate text-left">
            <RadixSelect.Value placeholder={placeholder} />
          </span>
          <RadixSelect.Icon className="shrink-0">
            <ChevronDown className="size-[var(--pt-icon)] text-[var(--pt-text-tertiary)]" />
          </RadixSelect.Icon>
        </RadixSelect.Trigger>

        <RadixSelect.Portal>
          <RadixSelect.Content
            position="popper"
            sideOffset={4}
            className={cn(
              "z-[var(--pt-z-dropdown)] max-h-[320px] min-w-[var(--radix-select-trigger-width)]",
              "overflow-hidden rounded-[var(--pt-radius-5)] border border-[var(--pt-border-default)]",
              "bg-[var(--pt-bg-raised)] shadow-[var(--pt-shadow-2)]",
            )}
          >
            <RadixSelect.ScrollUpButton className="flex h-[20px] items-center justify-center text-[var(--pt-text-tertiary)]">
              <ChevronUp className="size-[var(--pt-icon)]" />
            </RadixSelect.ScrollUpButton>
            <RadixSelect.Viewport className="p-[var(--pt-space-4)]">
              {options.map((option) => (
                <RadixSelect.Item
                  key={option.value}
                  value={toItemValue(option.value)}
                  disabled={option.disabled}
                  className={cn(
                    "relative flex h-[var(--pt-row-h)] cursor-default select-none items-center",
                    "gap-[var(--pt-space-6)] rounded-[var(--pt-radius-2)] pl-[var(--pt-space-8)] pr-[24px]",
                    "text-[var(--pt-font-ui)] text-[var(--pt-text-primary)] outline-none",
                    "data-[highlighted]:bg-[var(--pt-bg-hover)]",
                    "data-[state=checked]:bg-[var(--pt-bg-selected)]",
                    "data-[disabled]:text-[var(--pt-text-disabled)]",
                  )}
                >
                  <RadixSelect.ItemText>{option.label}</RadixSelect.ItemText>
                  <RadixSelect.ItemIndicator className="absolute right-[var(--pt-space-8)]">
                    <Check className="size-[var(--pt-icon)] text-[var(--pt-accent-text)]" />
                  </RadixSelect.ItemIndicator>
                </RadixSelect.Item>
              ))}
            </RadixSelect.Viewport>
            <RadixSelect.ScrollDownButton className="flex h-[20px] items-center justify-center text-[var(--pt-text-tertiary)]">
              <ChevronDown className="size-[var(--pt-icon)]" />
            </RadixSelect.ScrollDownButton>
          </RadixSelect.Content>
        </RadixSelect.Portal>
      </RadixSelect.Root>
    </div>
  );
}
